import { useSnackbar } from 'notistack';
import { useCallback, useState } from 'react';
import { uploadApi } from 'src/api/upload';
import { publicUploadApi } from 'src/api/upload-public';
import { guestVisitApi } from 'src/api/visit-guest';
import { useAuthContext } from 'src/auth/useAuthContext';
import { translateKey } from 'src/locales/translate';


type UploadStatus = {
  downloadURLs: {
    name: string;
    url: string;
    progress: number;
    status?: string;
    originalName?: string;
    uploadToken?: string;
  }[];
  upload: (files: File[]) => void;
  error: boolean;
  reset: () => void;
  remove: (fileName: string) => void;
  setInitialFiles: (
    files: {
      name: string;
      url: string;
      progress: number;
      status?: string;
      originalName?: string;
      uploadToken?: string;
    }[],
  ) => void;
};

type UseUploadDocumentsParams = {
  profileId?: string;
  visitId?: string;
  usePublicEndpoint?: boolean;
  guestSessionId?: string;
};

export const useUploadDocuments = (
  params?: UseUploadDocumentsParams,
): UploadStatus => {
  const { profileId, visitId, usePublicEndpoint = false, guestSessionId } = params || {};
  const [error, setError] = useState(false);
  const [downloadURLs, setDownloadURLs] = useState<
    UploadStatus['downloadURLs']
  >([]);

  const { enqueueSnackbar } = useSnackbar();
  const { isAuthenticated } = useAuthContext();

  const upload = useCallback(
    async (files: File[]) => {
      const filesWithTempNames = files.map((file, index) => ({
        file,
        tempFileName: `temp_${new Date().getTime()}_${index}_${file.name}`,
        originalName: file.name,
      }));

      setDownloadURLs((prevURLs) => [
        ...prevURLs,
        ...filesWithTempNames.map(({ tempFileName, originalName }) => ({
          name: tempFileName,
          url: '',
          progress: 0,
          status: 'Oczekiwanie...',
          originalName,
        })),
      ]);

      for (const { file, tempFileName } of filesWithTempNames) {
        setDownloadURLs((prevURLs) =>
          prevURLs.map((item) =>
            item.name === tempFileName
              ? { ...item, status: 'Przygotowywanie pliku...' }
              : item,
          ),
        );

        try {
          // Set progress to uploading (30%)
          setDownloadURLs((prevURLs) =>
            prevURLs.map((item) =>
              item.name === tempFileName
                ? { ...item, progress: 30, status: translateKey("hardcoded.wysylanie_pliku") }
                : item,
            ),
          );

          // Entity uploads use their authorized profile/visit; booking uploads
          // remain unbound until submit. Keep the server's upload proof with
          // the file through every route and progress update.
          let filename: string;
          let url: string;
          let uploadToken: string | undefined;

          const hasOwnerContext = Boolean(profileId || visitId);

          if (isAuthenticated && !usePublicEndpoint && hasOwnerContext) {
            // Authenticated private upload — main BE.
            // Backend enforces XOR(profileId, visitId); we already have exactly one.
            const response = await uploadApi.uploadControllerUploadPrivate({
              file,
              ...(profileId ? { profileId } : {}),
              ...(visitId ? { visitId } : {}),
            });
            uploadToken = response.data.uploadToken;
            filename = response.data.filename;
            // For private uploads the backend returns the storage path as `url`;
            // use it directly — VisitDetailsEditModal's openFile handler will
            // fetch a signed URL when it does not start with 'http'.
            url = response.data.url;
          } else if (usePublicEndpoint && visitId) {
            // Post-magic-link guest visit upload — public BE, visit-bound private file.
            // Must use raw axios with X-Guest-Session; the shared client only carries
            // Firebase auth headers and would 401 on GuestSessionGuard.
            if (!guestSessionId) {
              throw new Error('Guest visit upload requires a guest session id');
            }
            const response = await guestVisitApi.uploadVisitPrivate(visitId, guestSessionId, file);
            uploadToken = response.data.uploadToken;
            filename = response.data.filename ?? '';
            // BE returns a fresh short-lived signed URL so the FE can preview
            // the file before the documentsToAdd / updateDetails claim creates
            // the Document row. Fall back to filename for older BE versions
            // where the response only carried filename.
            url = response.data.url ?? filename;
          } else if (usePublicEndpoint) {
            // Org-image public upload — main BE (Firebase-authenticated), public bucket, returns a real public URL.
            const response = await uploadApi.uploadControllerUploadPublic({ file });
            uploadToken = response.data.uploadToken;
            filename = response.data.filename;
            url = response.data.url;
          } else if (isAuthenticated) {
            // Authenticated booking-time upload — auth user uploading attachments
            // before a profile/visit exists. Routes through the MAIN BE so the upload
            // is tied to the Firebase identity. Filename gets claimed at schedule submit.
            const response = await uploadApi.uploadControllerUploadBookingAttachment({
              file,
            });
            uploadToken = response.data.uploadToken;
            filename = response.data.filename ?? '';
            url = response.data.url ?? filename;
          } else {
            // Guest booking-time upload (no Firebase identity) — public BE, private
            // bucket. Filename gets claimed at schedule submit via attachments[].
            const response = await publicUploadApi.uploadControllerUploadGuestAttachment({ file });
            uploadToken = response.data.uploadToken;
            filename = response.data.filename ?? '';
            // BE returns a fresh short-lived signed URL so guests can preview
            // their booking attachments before submit. Fall back to filename
            // for older BE versions / robustness.
            url = response.data.url ?? filename;
          }

          const result = { filename, url, uploadToken };

          // Set progress to processing (60%)
          setDownloadURLs((prevURLs) =>
            prevURLs.map((item) =>
              item.name === tempFileName
                ? {
                    name: result.filename,
                    url: result.url,
                    uploadToken: result.uploadToken,
                    progress: 60,
                    status: translateKey("search.fileProcessing"),
                    originalName: item.originalName,
                  }
                : item,
            ),
          );

          // Add 5-second processing delay with progress updates
          // Simulate processing with progress increments
          await new Promise((resolve) => {
            let currentProgress = 60;
            const interval = setInterval(() => {
              currentProgress += 8; // Increment by 8% every second (5 seconds = 40% total)
              if (currentProgress >= 100) {
                currentProgress = 100;
                clearInterval(interval);
                resolve(undefined);
              }
              setDownloadURLs((prevURLs) =>
                prevURLs.map((item) =>
                  item.name === result.filename
                    ? {
                        ...item,
                        progress: currentProgress,
                        status: translateKey("search.fileProcessing"),
                        originalName: item.originalName,
                      }
                    : item,
                ),
              );
            }, 625); // Update every 625ms for smooth progress (5 seconds / 8 steps)
          });

          // Final update with 100% progress
          setDownloadURLs((prevURLs) =>
            prevURLs.map((item) =>
              item.name === result.filename
                ? {
                    ...item,
                    progress: 100,
                    status: translateKey("visit.stepsCompleteBadge"),
                    originalName: item.originalName,
                  }
                : item,
            ),
          );
        } catch (error: any) {
          setError(true);
          const errorMessage =
            error?.response?.data?.message || translateKey("hardcoded.nie_moglismy_dodac_zalacznikow");
          enqueueSnackbar(errorMessage, {
            variant: 'error',
          });

          // Remove failed upload from list
          setDownloadURLs((prevURLs) =>
            prevURLs.filter((item) => item.name !== tempFileName),
          );
        }
      }
    },
    [enqueueSnackbar, isAuthenticated, profileId, visitId, usePublicEndpoint, guestSessionId],
  );

  const remove = useCallback((fileName: string) => {
    // Simply remove from the list - backend cleanup can be handled separately if needed
    setDownloadURLs((prevURLs) =>
      prevURLs.filter((file) => file.name !== fileName),
    );
  }, []);

  const reset = () => setDownloadURLs([]);

  const setInitialFiles = useCallback(
    (files: { name: string; url: string; progress: number }[]) => {
      setDownloadURLs(files);
    },
    [],
  );

  return {
    downloadURLs,
    upload,
    reset,
    error,
    remove,
    setInitialFiles,
  };
};
